import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Circle, MessageSquare, AlertCircle, RefreshCw, Bell, BellOff } from 'lucide-react';
import { useWorkspace } from '../lib/workspace.jsx';
import { initials, shortTime } from '../lib/format.js';

/** "3m" — how long this visitor has been attached. */
function attachedFor(connectedAt) {
  const seconds = Math.max(0, (Date.now() - new Date(connectedAt)) / 1000);
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
}

/**
 * Who is on the widget right now.
 *
 * State lives in the workspace and is driven by `presence:changed` events, so
 * this list updates the instant someone arrives or leaves. It is seeded by a
 * single fetch per app — no polling, which is what used to make the table
 * appear and then blank itself between snapshots.
 */
export function OnlinePanel() {
  const {
    activeApp,
    allApps,
    onlineVisitors,
    presence,
    reloadPresence,
    conversations,
    setConversationMuted,
  } = useWorkspace();
  const navigate = useNavigate();
  const [, setTick] = useState(0);

  // Only the "online 3m" labels are time-dependent; re-render them minutely.
  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(timer);
  }, []);

  const conversationFor = (visitor) =>
    conversations.find((c) => c.id === visitor.conversationId) ?? null;

  const toggleMute = async (visitor) => {
    const conversation = conversationFor(visitor);
    if (!conversation) return;
    await setConversationMuted(conversation.id, !conversation.notifications?.agentMuted);
  };

  return (
    <div className="card">
      <div className="card__head">
        <span className="card__title">
          <Circle size={9} className="online-dot" /> Online now
        </span>
        <span className="card__meta mono">
          {onlineVisitors.length} visitor{onlineVisitors.length === 1 ? '' : 's'} ·{' '}
          {presence.agents.length} agent{presence.agents.length === 1 ? '' : 's'}
        </span>
        <button
          type="button"
          className="icon-button"
          onClick={reloadPresence}
          aria-label="Refresh presence"
        >
          <RefreshCw size={13} />
        </button>
      </div>

      {onlineVisitors.length === 0 && (
        <div className="table__empty">
          No visitors connected to{' '}
          <strong>{activeApp?.name ?? (allApps ? 'any of your apps' : 'this app')}</strong> right
          now. A merchant counts as online while the widget is loaded on their screen — check you
          are scoped to the app whose public key that widget uses.
        </div>
      )}

      {onlineVisitors.length > 0 && (
        <div className="online">
          {onlineVisitors.map((visitor) => {
            const conversation = conversationFor(visitor);
            const muted = Boolean(conversation?.notifications?.agentMuted);

            return (
              <div className="online__row" key={visitor.visitorId}>
                <span className="online__pip" />
                <span className="avatar">
                  {initials(visitor.name || visitor.visitorId.slice(2, 4))}
                </span>

                <span className="online__text">
                  <span className="online__name">
                    {visitor.name || visitor.visitorId}
                    {(visitor.hasEmergency || conversation?.hasEmergency) && (
                      <span className="flag flag--danger">
                        <AlertCircle size={9} /> SOS
                      </span>
                    )}
                    {conversation?.unread.agent > 0 && (
                      <span className="flag flag--count">{conversation.unread.agent}</span>
                    )}
                    {muted && <span className="flag flag--muted">MUTED</span>}
                  </span>
                  <span className="online__meta">
                    {visitor.email || 'No email captured'}
                    <span className="dot-sep">·</span>
                    online {attachedFor(visitor.connectedAt)}
                    {conversation?.lastMessageAt && (
                      <>
                        <span className="dot-sep">·</span>
                        last message {shortTime(conversation.lastMessageAt)}
                      </>
                    )}
                  </span>
                </span>

                <button
                  type="button"
                  className={`icon-button${muted ? ' is-danger' : ''}`}
                  onClick={() => toggleMute(visitor)}
                  disabled={!conversation}
                  title={
                    muted
                      ? 'Notifications muted for this visitor — click to unmute'
                      : 'Mute notifications for this visitor'
                  }
                >
                  {muted ? <BellOff size={13} /> : <Bell size={13} />}
                </button>

                <button
                  type="button"
                  className="button button--primary"
                  onClick={() => navigate(`/chat?c=${visitor.conversationId}`)}
                  disabled={!visitor.conversationId}
                >
                  <MessageSquare size={13} /> Message
                </button>
              </div>
            );
          })}
        </div>
      )}

      {presence.agents.length > 0 && (
        <>
          <div className="card__head card__head--sub">
            <span className="card__title">Agents on shift</span>
          </div>
          <div className="online">
            {presence.agents.map((agent) => (
              <div className="online__row online__row--compact" key={agent.userId}>
                <span className="online__pip" />
                <span className="avatar">{initials(agent.name || agent.email)}</span>
                <span className="online__text">
                  <span className="online__name">
                    {agent.name || agent.email}
                    {agent.isYou && <span className="flag flag--muted">YOU</span>}
                  </span>
                  <span className="online__meta">online {attachedFor(agent.connectedAt)}</span>
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {!presence.clusterWide && (
        <div className="card__foot muted">
          Presence covers this server process only. Set <code>REDIS_URL</code> so the Socket.IO
          adapter spans instances, or these counts will undercount behind a load balancer.
        </div>
      )}
    </div>
  );
}

export default OnlinePanel;
